import { Box, Flex, Toast } from '@modusign/design-system';
import { useCreateTemplate } from '@modusign/openapi-client-template';
import { CreateTemplateRequestDto } from '@modusign/openapi-client-template/model';
import { useDocument } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/setup';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { TextFieldFontLoader } from '@app/components/TextField';
import TemplateFieldFooter from '@app/containers/TemplateFieldFooter';
import RequesterAttachmentsCard from '@app/features/apply-react-signing/components/RequesterAttachmentCard';
import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { templateMutationErrorHandler } from '@app/features/apply-react-signing/libs/template/errorHandler';
import TemplateHeader from '@app/features/template/components/TemplateHeader';
import TemplateLabelInputCard from '@app/features/template/components/TemplateLabelInputCard';
import TemplateLoadingSpinner from '@app/features/template/components/TemplateLoadingSpinner';
import TemplateSignerDetailSetUpCard from '@app/features/template/components/TemplateSignerDetailSetUpCard';
import TemplateTitleInputCard from '@app/features/template/components/TemplateTitleInputCard';
import useTemplateOrdering from '@app/features/template/hooks/useTemplateOrdering';
import validateTemplateTitle from '@app/features/template/libs/validateTemplateTitle';
import { usePDF } from '@app/hooks/usePDF';
import useUsageCount from '@app/hooks/useUsageCount';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import embeddedService from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import { pdfToBase64 } from '@app/libs/pdf/pdfToBase64';
import PDFDocumentPageLayout from '@app/libs/reactPdfJs/components/PDFDocumentPageLayout';
import { zoomIn, zoomOut } from '@app/libs/reactPdfJs/store/actions';
import workspaceService from '@app/libs/workspaceService';
import getTemplateAttachmentLimitReached from '@app/store/selectors/getTemplateAttachmentLimitReached';
import { templateLoading, templateLoadingFinish } from '@app/store/template/actions';
import { fileIdSelector, labelIdsSelector } from '@app/store/template/selectors';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';

interface TemplateGeneralSetupPageProps {
  handlePrevRouteClick: () => void;
}

function TemplateGeneralSetupPage({ handlePrevRouteClick }: TemplateGeneralSetupPageProps) {
  const dispatch = useDispatch();
  const { templateCount } = useUsageCount();
  const { initOrdering } = useTemplateOrdering();
  const hasAttachmentLimitReached = useSelector(getTemplateAttachmentLimitReached);
  const [error, setError] = useState('');
  const fileId = useSelector(fileIdSelector);
  const labelIds = useSelector(labelIdsSelector);
  const history = useHistory();
  const { signingState } = useSigningState();
  const { document, setTitle } = useDocument();
  const { pdf } = usePDF();

  const handleTitleValidationError = (title: string) => {
    const { message } = validateTemplateTitle(title);
    setError(message);
  };

  useEffect(() => {
    handleTitleValidationError(document.title || '');
  }, [document.title]);

  const { mutate: createTemplate } = useCreateTemplate({
    mutation: {
      onSuccess: ({ id }) => {
        pushDataLayer({ event: 'createTemplate' });
        initOrdering();
        dispatch(templateLoadingFinish());
        Toast.success({ description: '새로운 템플릿을 만들었습니다.' });
        if (embeddedService.isEmbedded) {
          redirect(embeddedService.redirectUrl, {
            query: {
              templateId: id,
              eventType: 'template_created',
            },
          });
        } else {
          history.push('/templates');
        }
      },
      onError: (error, { data }) => {
        dispatch(templateLoadingFinish());
        templateMutationErrorHandler(dispatch, error, data, templateCount.limit);
      },
    },
  });

  const handleNextStepClick = async () => {
    dispatch(templateLoading());
    let newFileId = fileId;
    if (pdf) {
      const pdfAsBase64 = await pdfToBase64(pdf);
      const { fileId: id } = await postDocumentUpload({ base64: pdfAsBase64 });
      newFileId = id;
    }
    const data = toDocumentModelForSetup(signingState);
    createTemplate({
      workspaceId: workspaceService.getWorkspaceId(),
      data: {
        attachments: data.attachments,
        participants: data.participants,
        fieldGroups: data.fieldGroups,
        fields: data.fields,
        verifications: data.verifications,
        title: document.title,
        file: {
          id: newFileId,
        },
        labelIds,
      } as CreateTemplateRequestDto,
    });
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const handleZoomIn = () => {
    dispatch(zoomIn());
  };

  const handleZoomOut = () => {
    dispatch(zoomOut());
  };

  return (
    <PDFDocumentPageLayout>
      <TextFieldFontLoader />
      <TemplateLoadingSpinner />
      <TemplateHeader
        currentStepIndex={3}
        isExchangeDocumentVisible
        progressStep="templateGeneralSetup"
        onPDFZoomIn={handleZoomIn}
        onPDFZoomOut={handleZoomOut}
      />

      <Box flex="1" minHeight="0" overflow="auto">
        <Flex flexDirection="column" gap={4} maxWidth="xs" mx="auto" pb="86px" pt={4} px={4}>
          <TemplateTitleInputCard
            error={error}
            handleTitleChange={handleTitleChange}
            title={document.title || ''}
          />{' '}
          <RequesterAttachmentsCard
            displayDataLabel
            hasAttachmentLimitReached={hasAttachmentLimitReached}
          />
          <TemplateLabelInputCard labelIds={labelIds} />
          <TemplateSignerDetailSetUpCard />
        </Flex>
      </Box>
      <TemplateFieldFooter
        backButtonText="< 이전 단계로"
        error={!!error}
        nextButtonText="템플릿 저장 >"
        onBackStepClick={handlePrevRouteClick}
        onNextStepClick={handleNextStepClick}
      />
    </PDFDocumentPageLayout>
  );
}

export default TemplateGeneralSetupPage;
